// src/components/frontpage/columns/DirectoryColumn.jsx
//
// Directory Mode Column for the Locality desktop front page (Phase 2, June 2026).
// Holds the content that previously lived in SidebarLocality, now as one of four equal
// Mode Columns. Mounts on the shared ModeColumn shell; data comes from the shared
// useDirectoryListings hook (same fetch the mobile sidebar uses).
//
// Column layout (top → bottom): slim locality strip (weather + town image) sits ABOVE the
// ModeColumn so the "Directory" header reads as a label under the locality image. Below it,
// the ModeColumn carries the header → scroll body → pinned footer (Suggest CTA · about
// paragraph, signed-out only · logo + tagline on one line). Footer content is passed to
// ModeColumn's `footer` slot, so it stays pinned at the bottom of the full-height column
// instead of scrolling with the body.
//
// Column shell brief, Scope 2 (16 Aug): the Suggest CTA moved from the scroll body into this
// pinned footer, immediately above the logo block — its label follows the open section.
// Scope 3, as amended (16 Aug): the scroll body is now an EXCLUSIVE concertina — DirectoryNav /
// ContactList / UtilityCategoryList are always-mounted, always-headed (their own SectionTitle is
// now the click target), but only the open section's body renders; exactly one is open at all
// times (Directory on load), and the open one cannot be closed, only switched away from.
// Border-and-footer-corrections brief (16 Aug): the footer order was corrected again (paragraph,
// signed-out only, then logo + tagline side by side — was tagline / paragraph / logo stacked),
// and the section-level open border was removed in favour of one on the open entry's detail
// panel only (see DirectorySection.jsx's openEntryStyle).
//
// EYEBALL NOTES (defaults, Michael to confirm on localhost):
//   - The weather + town strip is the first thing to slim/drop if the column feels cramped.
//   - DirectoryNav uses drillStyle="accordion" (inline expansion, like mobile) — the accordion
//     "needs tuning" (shared with mobile); desktop inherits the same rough edges for now.
//   - Header has no "+" (suggest-a-listing keeps its dedicated dashed CTA in the pinned footer).
//     Wiring onAdd={handleSuggestNew} would add a "+" for header consistency — a one-line change.

import React, { useState, useCallback, useEffect } from 'react';
import { useTheme } from '../../../themes/ThemeProvider';
import { useTranslation } from '../../../i18n/useTranslation';
import SidebarContextWidget from '../../core/SidebarContextWidget';
import TownImage from '../TownImage';
import ContactList from '../ContactList';
import DirectoryNav from '../DirectoryNav';
import UtilityCategoryList from '../UtilityCategoryList';
import SuggestListingModal from '../SuggestListingModal';
import SuggestionReviewQueue from '../SuggestionReviewQueue';
import { useDirectoryListings } from '../../../hooks/useDirectoryListings';
import { useExpandInPlace } from '../../../hooks/useExpandInPlace';
import { useAuth } from '../../../contexts/AuthContext';
import ModeColumn from './ModeColumn';

const DirectoryColumn = ({
  selectedArea = null,
  currentUser = null,
  guestFallbackBoundaryId = null,   // pilot-anchor boundary for logged-out visitors (no selectedArea)
  visitBoundaryId = null,   // active temporary visit (Change 3) — wins over selectedArea/home
  userHomeLocationName,
  userHomeLocationCoords,
  hideColumnHeader = false,
  bodyFooter = null,  // optional content at the bottom of the scroll body (mobile: Install prompt)
  expandedLocalityStrip = false  // Desktop front page only: widens the town-image strip to fill
                                  // the column and swaps in the richer, expandable weather widget
                                  // (whose own place-name label is dropped unconditionally — see
                                  // WeatherWidgetExpandable.jsx). Formerly also gated a locality-
                                  // name headline above the image; removed (docs/briefs/2026-09-
                                  // 04-locality-column-headline-removal-fix.md) as redundant with
                                  // the main app header, which names the locality on every
                                  // account regardless of this flag.
}) => {
  const theme = useTheme();
  const { t } = useTranslation('frontpage');
  const { requireAuth, isLoggedIn } = useAuth();

  const {
    boundaryId,
    contacts,
    utilities,
    directoryListings,
    heroImageUrl,
    heroImageCredit,
    canReviewSuggestions,
    pendingSuggestions,
    setPendingSuggestions,
    refreshPending
  } = useDirectoryListings({ selectedArea, currentUser, guestFallbackBoundaryId, visitBoundaryId });

  // Modal UI state (component-owned)
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [suggestModalListing, setSuggestModalListing] = useState(null);
  const [showReviewQueue, setShowReviewQueue] = useState(false);

  // Single-open entry card across ALL directory sections. One shared open-entry id lives here
  // (via useExpandInPlace, uncontrolled at this top level — see src/hooks/useExpandInPlace.js);
  // DirectoryNav / ContactList / UtilityCategoryList each render their detail card off this value
  // in place of their own local open state, so opening any card closes every other open card
  // (including cards in sibling sections). Listing/contact ids are directory_listings UUIDs, so
  // one shared id suffices.
  const listingGroup = useExpandInPlace();
  const openListingId = listingGroup.openId;
  const handleToggleListing = useCallback((id) => {
    listingGroup.toggle(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingGroup.toggle]);

  // Single-open CATEGORY across all sections — the level above the card. One shared open-category
  // key, namespaced per section ('directory:<id>' vs 'utility:<id>') so a same-named category in
  // two sections can never collide on one shared value. Opening/switching/closing a category
  // clears the open card (rule 2): the card lived inside the category being left. Opening a
  // *listing* does NOT touch the category (the card lives inside the still-open category).
  const categoryGroup = useExpandInPlace();
  const openCategoryKey = categoryGroup.openId;
  const handleToggleCategory = useCallback((key) => {
    categoryGroup.toggle(key);
    listingGroup.toggle(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryGroup.toggle, listingGroup.toggle]);

  // Official Contacts are TOP-LEVEL listings — no parent category. Opening one behaves like the
  // shared card toggle (single card open across sections) AND clears any open category, since the
  // contact does not live inside one. Directory/utility listings keep using handleToggleListing,
  // which preserves their parent category. (The reverse — a category open clearing the card — is
  // already handled by handleToggleCategory above.)
  const handleToggleContact = useCallback((id) => {
    listingGroup.toggle(id);
    categoryGroup.toggle(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingGroup.toggle, categoryGroup.toggle]);

  // Section-level open state (Directory column shell brief, Scope 3) — one level ABOVE the
  // category/listing state above: exactly one of the three sections (directory / official_contact
  // / utility) is open at a time, Directory open on load, and the open section cannot be closed,
  // only switched away from (handleToggleSection below no-ops on the already-open id, rather than
  // using sectionGroup.toggle directly, which would otherwise close-to-null on a repeat click).
  // Switching sections also clears the deeper category/listing state — the card/category being
  // left is no longer visible, so a stale open id there would be invisible state, not a feature.
  const sectionGroup = useExpandInPlace({ defaultOpenId: 'directory' });
  const openSection = sectionGroup.openId;
  const handleToggleSection = useCallback((id) => {
    if (id === sectionGroup.openId) return;
    sectionGroup.toggle(id);
    categoryGroup.toggle(null);
    listingGroup.toggle(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionGroup.openId, sectionGroup.toggle, categoryGroup.toggle, listingGroup.toggle]);

  // openCategoryId — the bare id (without its section namespace) for the currently-open
  // Directory-section category, used only as SuggestListingModal's initialCategory. Blank outside
  // the directory section or with no category open (namespaces defined in the category/listing
  // state above).
  const DIRECTORY_CAT_NS = 'directory:';
  const openCategoryId = openCategoryKey?.startsWith(DIRECTORY_CAT_NS)
    ? openCategoryKey.slice(DIRECTORY_CAT_NS.length)
    : '';
  const SUGGEST_LABEL_KEYS = {
    directory: 'suggest_listing',
    utility: 'suggest_utility',
    official_contact: 'suggest_contact'
  };
  const suggestLabelKey = SUGGEST_LABEL_KEYS[openSection];

  // Gate the form-open boundary behind auth — covers all three entry points (header
  // ournet:open-suggest-listing event, body CTA, and the per-listing correction link).
  // Guests get the login hint instead of an open form they can't submit.
  const handleSuggestNew = useCallback(() => {
    requireAuth('gate_body_suggest_listing', () => {
      setSuggestModalListing(null);
      setShowSuggestModal(true);
    });
  }, [requireAuth]);

  const handleSuggestCorrection = useCallback((listing) => {
    requireAuth('gate_body_suggest_listing', () => {
      setSuggestModalListing(listing);
      setShowSuggestModal(true);
    });
  }, [requireAuth]);

  // Mobile: the front-page header's "+ Add a listing" create pill fires this event
  // (the create control lives in the header, the modal lives here). Mirrors the
  // ournet:open-swap-create pattern. Desktop never dispatches it — harmless.
  useEffect(() => {
    const handler = () => handleSuggestNew();
    window.addEventListener('ournet:open-suggest-listing', handler);
    return () => window.removeEventListener('ournet:open-suggest-listing', handler);
  }, [handleSuggestNew]);

  // The mobile sub-header (search only — the Suggest CTA stays in ModeColumn's pinned footer
  // slot below and does not move here, per the mobile-tab-subheaders brief §1.3) is composed by
  // the caller (MobileFrontPage.jsx's 'directory' case), OUTSIDE this column's own horizontally-
  // padded wrapper, not here — this column is mounted with 12px of horizontal padding on mobile
  // (see the comment at that call site), unlike the other four tabs, so a sub-header rendered
  // inside it would be narrower than theirs (addendum 1, defect 1: search not full width).

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      minWidth: 0
    }}>
      {/* Locality strip — town image + weather, ABOVE the Directory header. flex-shrink:0 keeps
          it at its natural height at the top of the column. No locality-name text here — the
          main app header already names the locality on every account (docs/briefs/2026-09-04-
          locality-column-headline-removal-fix.md); a prior headline div duplicating it here was
          removed rather than corrected. */}
      <div style={{ flexShrink: 0, marginBottom: '12px' }}>
        <TownImage
          src={heroImageUrl}
          credit={heroImageCredit}
          compact={true}
          fullWidth={expandedLocalityStrip}
        />
        <SidebarContextWidget
          currentContext="locality"
          userHomeLocationName={userHomeLocationName}
          userHomeLocationCoords={userHomeLocationCoords}
          compact={true}
          hideLocationName={expandedLocalityStrip}
          useExpandableWeather={expandedLocalityStrip}
        />
      </div>

      {/* ModeColumn fills the rest of the column (flex:1); its footer stays pinned at the
          bottom. height:100% resolves against this flex:1/minHeight:0 wrapper — the same
          pattern LocalityFrontPage uses for MultiViewGrid. */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <ModeColumn
          title={t('directory')}
          themeColor={theme.primary}
          hideColumnHeader={hideColumnHeader}
          onAdd={undefined}
          footer={
            /* Pinned footer branding — relocated from the retired sidebar-clipped footer,
               now out of the scroll body so it stays at the bottom of the full-height column.
               Column shell brief, Scope 2: the Suggest CTA moved here too (was inside the
               scrolling ModeColumn body, below UtilityCategoryList) — it sits immediately above
               the paragraph/logo/tagline block, outside the scrolling region, so it never scrolls
               out of view.
               Order (border-and-footer-corrections brief §2, correcting Scope 3's stacked
               tagline / paragraph / logo): the "About OurNet" paragraph (logged-out only —
               onboarding copy is noise to a signed-in user, same convention GroupsColumn already
               uses for its own welcome message), then the logo and tagline side by side on one
               line, logo left. */
            <div style={{
              paddingTop: '12px',
              paddingBottom: '4px',
              borderTop: '1px solid #d8e2ee'
            }}>
              {/* Suggest CTA — shown whenever there's a boundary (guests included; handleSuggestNew
                  gates on auth and shows the login hint on click, so this still renders sensibly
                  for a logged-out visitor on an empty locality). Label follows the conceptually
                  open section (derived above from existing category/listing state — Scope 2 does
                  not add new state for this). */}
              {boundaryId && (
                <button
                  onClick={handleSuggestNew}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    width: '100%',
                    padding: '8px 12px',
                    marginBottom: '12px',
                    background: 'white',
                    border: '1px dashed #a0c4e8',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontFamily: 'OpenSauceSans, sans-serif',
                    fontSize: '12px',
                    color: theme.primary,
                    fontWeight: '600',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f7ff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
                >
                  + {t(suggestLabelKey)}
                </button>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                {/* "About OurNet" — logged-out only (GroupsColumn's own welcome-message
                    convention: onboarding copy, noise once a user is signed in). Collapses
                    cleanly with no reserved space when hidden — the flex column's own gap
                    simply closes up around it, no placeholder needed. */}
                {!isLoggedIn && (
                  <p style={{
                    margin: 0,
                    fontSize: '11px',
                    lineHeight: '1.4',
                    color: '#5b6b82'
                  }}>
                    {t('about_ournet_short')}
                  </p>
                )}

                {/* Logo + tagline, one line, logo left (border-and-footer-corrections brief §2). */}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                  <img
                    src="/images/logos/logo-ournet.png"
                    alt="OurNet Logo"
                    style={{ width: '48px', height: '47px' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: theme.primary,
                    letterSpacing: '0.5px'
                  }}>
                    {t('ui:tagline')}
                  </div>
                </div>
              </div>
            </div>
          }
          overlay={
            <>
              {/* Suggest listing modal */}
              {showSuggestModal && (
                <SuggestListingModal
                  boundaryId={boundaryId}
                  listing={suggestModalListing}
                  initialSection={openSection}
                  initialCategory={openCategoryId}
                  canReviewSuggestions={canReviewSuggestions}
                  onClose={() => setShowSuggestModal(false)}
                  onSubmitted={() => {
                    setShowSuggestModal(false);
                    refreshPending();
                  }}
                />
              )}

              {/* Suggestion review queue modal (Stewards + admins) */}
              {showReviewQueue && (
                <SuggestionReviewQueue
                  onClose={() => setShowReviewQueue(false)}
                  onCountChange={(delta) => {
                    setPendingSuggestions(prev => Math.max(0, prev + delta));
                  }}
                />
              )}
            </>
          }
        >
          {/* 8-category directory navigation — one of three exclusive concertina sections
              (Scope 3); its own SectionTitle is now the section's click target. */}
          <DirectoryNav
            compact={true}
            listings={directoryListings}
            onSuggestCorrection={handleSuggestCorrection}
            drillStyle="accordion"
            openListingId={openListingId}
            onToggleListing={handleToggleListing}
            openCategoryKey={openCategoryKey}
            onToggleCategory={handleToggleCategory}
            isOpen={openSection === 'directory'}
            onToggleSection={() => handleToggleSection('directory')}
          />

          {/* Official contacts — merged list: the locality's own plus every ancestor boundary's
              (município-tier ruling), pre-sorted by directory_authority_categories rank then
              alphabetically (directoryService.js). No separate heading per tier — position and
              the entity names themselves ("Câmara Municipal de …" / "Junta de Freguesia de …")
              carry the authority distinction, including the same-place-name case (e.g. Alcobaça
              freguesia's junta next to Alcobaça concelho's câmara: adjacent, distinct by name
              prefix and fixed rank order). Top-level listings, no category: opening one clears
              any open category via handleToggleContact. */}
          <ContactList
            title={t('official_contacts')}
            contacts={contacts}
            compact={true}
            drillStyle="accordion"
            openListingId={openListingId}
            onToggleListing={handleToggleContact}
            onSuggestCorrection={handleSuggestCorrection}
            isOpen={openSection === 'official_contact'}
            onToggleSection={() => handleToggleSection('official_contact')}
          />

          {/* Utility contacts — category-based, inline accordion drill */}
          <UtilityCategoryList
            categories={utilities}
            compact={true}
            drillStyle="accordion"
            openCategoryKey={openCategoryKey}
            onToggleCategory={handleToggleCategory}
            openListingId={openListingId}
            onToggleListing={handleToggleListing}
            onSuggestCorrection={handleSuggestCorrection}
            isOpen={openSection === 'utility'}
            onToggleSection={() => handleToggleSection('utility')}
          />

          {/* Steward / admin pending suggestions badge — clickable to open review queue */}
          {canReviewSuggestions && pendingSuggestions > 0 && (
            <button
              onClick={() => setShowReviewQueue(true)}
              style={{
                display: 'block',
                width: '100%',
                marginTop: '8px',
                padding: '6px 10px',
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '6px',
                fontFamily: 'OpenSauceSans, sans-serif',
                fontSize: '11px',
                color: '#856404',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.15s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#fff0b3'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff3cd'; }}
            >
              {pendingSuggestions} {t('pending_suggestions')}
            </button>
          )}

          {/* Optional body footer — Install prompt on mobile (desktop passes nothing) */}
          {bodyFooter && (
            <div style={{ marginTop: '16px' }}>
              {bodyFooter}
            </div>
          )}
        </ModeColumn>
      </div>
    </div>
  );
};

export default DirectoryColumn;
